import { TFunction } from 'i18next';

import { QueueSheetManagementRows } from '../../QueueSheetManagement/interface/QueueSheetInterface';
import { BookingMode } from '../interface/DetailedBookingInterface';
import { BookingStep, DetailedFormType } from './DetailedBookingConstants';

export const FORM_FIELDS = {
  AE_NAME: 'aeName',
  AM_NAME: 'amName',
  CATEGORY: 'category',
  ORG: 'org',
  RODEO_AD_NAME: 'rodeoAdName',
  BRAND: 'brand',
  TYPE_OF_PURCHASE: 'typeOfPurchase',
  EXACT_BILLING_VALUE: 'exactBillingValue',
  SF_OPPORTUNITY_ID: 'sfOpportunityId',
  RO: 'ro',
  BRAND_CONFIRMATION: 'brandConfirmation',
  COMMENTS_BY_POC: 'commentsByPoc',
  CREATIVES: 'creatives',
  GTM_STATUS: 'gtmStatus',
  VALUE_BLOCKED_BY_LINE_ITEM: 'valueBlockedByLineItem',
  IMPRESSIONS_BLOCKED_OVER_CONTEXTUAL_NODES:
    'impressionsBlockedOverContextualNodes',
  IMPRESSIONS: 'impressions',
  APPROX_SHARE_BLOCKED_OVER_NODE: 'approxShareBlockedOverNode',
  CONTEXTUAL_NODE: 'contextualNode',
  SOV_PERCENTAGE_FOR_CONTEXTUAL_GP: 'sovPercentageForContextualGP',
  IMPRESSION_BOOKED: 'impressionBooked',
  VALUE_BOOKED: 'valueBooked',
  GP_SLOT: 'gpSlot',
  SOV_PERCENTAGE_FOR_GP: 'sovPercentageForGP',
  ENTRY_ID: 'entryId',
  MODIFIED_AT: 'modifiedAt',
  MODIFIED_BY: 'modifiedBy',
  SLOT_NAMES: 'slotNames',
} as const;


export const BASE_EDITABLE_FIELDS = [
  FORM_FIELDS.RODEO_AD_NAME,
  FORM_FIELDS.BRAND,
  FORM_FIELDS.EXACT_BILLING_VALUE,
  FORM_FIELDS.SF_OPPORTUNITY_ID,
  FORM_FIELDS.RO,
  FORM_FIELDS.BRAND_CONFIRMATION,
  FORM_FIELDS.COMMENTS_BY_POC,
  FORM_FIELDS.CREATIVES,
];

export const SEARCHABLE_BOOKING_FIELDS: Record<
  keyof QueueSheetManagementRows,
  string
> = {
  entryId: 'Entry ID',
  brand: 'Brand',
  aeName: 'Account Executive',
  amName: 'Account Manager',
  category: 'Category',
  typeOfPurchase: 'Type of Purchase',
  exactBillingValue: 'Billing Value',
  sfOpportunityId: 'SF Opportunity ID',
  ro: 'RO Status',
  brandConfirmation: 'Brand Confirmation',
  rodeoAdName: 'Rodeo Advertiser',
  commentsByPoc: 'Comments',
  creatives: 'Creatives',
  bookingId: 'Booking ID',
  modifiedAt: 'Last Updated At',
  modifiedBy: 'Last Updated By',
  slotNames: 'Slot Names',
  slotPackageNames: 'Slot Package Names',
  contextualNodes: 'Contextual Nodes'
};

/**
 * Used for field-specific filtering in search functionality
 */
export const BOOKING_FIELD_COLUMN_MAP: Record<string, keyof QueueSheetManagementRows> = {
  [FORM_FIELDS.BRAND]: FORM_FIELDS.BRAND,
  [FORM_FIELDS.AE_NAME]: FORM_FIELDS.AE_NAME,
  [FORM_FIELDS.AM_NAME]: FORM_FIELDS.AM_NAME,
  [FORM_FIELDS.CATEGORY]: FORM_FIELDS.CATEGORY,
  [FORM_FIELDS.TYPE_OF_PURCHASE]: FORM_FIELDS.TYPE_OF_PURCHASE,
  [FORM_FIELDS.SF_OPPORTUNITY_ID]: FORM_FIELDS.SF_OPPORTUNITY_ID,
  [FORM_FIELDS.RODEO_AD_NAME]: FORM_FIELDS.RODEO_AD_NAME,
  [FORM_FIELDS.MODIFIED_BY]: FORM_FIELDS.MODIFIED_BY,
  [FORM_FIELDS.COMMENTS_BY_POC]: FORM_FIELDS.COMMENTS_BY_POC,
  [FORM_FIELDS.SLOT_NAMES]: FORM_FIELDS.SLOT_NAMES,
  [FORM_FIELDS.EXACT_BILLING_VALUE]: FORM_FIELDS.EXACT_BILLING_VALUE,
  [FORM_FIELDS.CREATIVES]: FORM_FIELDS.CREATIVES,
  [FORM_FIELDS.ENTRY_ID]: FORM_FIELDS.ENTRY_ID,
  [FORM_FIELDS.GTM_STATUS]: FORM_FIELDS.GTM_STATUS,
};

export const getModalTitle = (formMode: BookingMode, currentStep: BookingStep, detailedFormType: DetailedFormType | null, t: TFunction<"translation", undefined, "translation">) => {
  if (formMode === BookingMode.CREATE) {
    if (currentStep === BookingStep.INITIAL)
      return t('create-booking-modal-title');

    return detailedFormType === DetailedFormType.IMP
      ? t('create-detailed-booking-imp-title')
      : t('create-detailed-booking-sov-title');
  } else {
    if (currentStep === BookingStep.INITIAL)
      return t('edit-booking-modal-title');

    return detailedFormType === DetailedFormType.IMP
      ? t('edit-detailed-booking-imp-title')
      : t('edit-detailed-booking-sov-title');
  }
};


export const GP_SLOT_FIELD = "gp-slot-field";

export const ADVERTISER_NOT_FOUND_TRANSLATION_KEY = 'advertiser-not-found';
export const ADVERTISER_NOT_FOUND_STRING = 'Advertiser not found';

export const RO_VALUE_MAX_LENGTH = 14;
